import { addDays, differenceInCalendarDays, endOfDay, format, startOfDay } from 'date-fns';
import { AlertTriangle, CalendarIcon, ChevronLeft, ChevronRight, ChevronsUpDown, Loader2, RefreshCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { FormatOptionsWithTZ, fromZonedTime, getTimezoneOffset, toZonedTime } from "date-fns-tz";

import { de } from 'date-fns/locale';
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Calendar } from '~/components/ui/calendar';
import { Checkbox } from '~/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '~/components/ui/command';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/label'; // Optional for better form structure
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '~/components/ui/popover';
import { ScrollArea } from '~/components/ui/scroll-area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { Skeleton } from '~/components/ui/skeleton';
import LocalDateTime from '~/lib/localdatetime';
import { cn } from '~/lib/utils';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { Step, useCreateOrModifyAppointmentStore } from '~/stores/create-appointment-store';
import { useUserStore } from '~/stores/user-store';
import { DateRange, OnSelectHandler } from 'react-day-picker';
import { PractitionerNameWithCategory } from '~/components/practitioners/PractitionerNameWithCategory';
import { RoomName } from '~/components/rooms/RoomName';
import { Separator } from '../ui/separator';

const uniqueOnId = <T extends { id: string | number }>(arr: T[]): T[] => {
    const seen = new Set<string | number>();
    return arr.filter((item) => {
        if (seen.has(item.id)) {
            return false;
        }
        seen.add(item.id);
        return true;
    });
}

const isSlotInTimePeriod = (slot: AvailableSlotData, period: 'morning' | 'midday' | 'evening'): boolean => {
    const startHour = parseInt(slot.from.format("HH"));

    if (period === 'morning') {
        return startHour < 12;
    } else if (period === 'midday') {
        return startHour >= 12 && startHour < 16;
    } else if (period === 'evening') {
        return startHour >= 16;
    }

    return false;
};

type AvailableSlotData = {
    timeslotPriorityCategory?: number;
    from: LocalDateTime;
    to: LocalDateTime;
    stepConstraints: {
        startLocalDateTime: LocalDateTime;
        endLocalDateTime: LocalDateTime;
        practitioners: { id: number; name: string | null; categoryId: number; categoryName: string }[];
        rooms: { id: number; name: string | null; categoryId: number; categoryName: string, equipment?: { id: number; name: string }[] }[];
        suggestedRoomId: number | null;
        equipment: { id: number; name: string | null; categoryId: number; categoryName: string }[];
    }[]; // idx of step in order of minuteStart
};

type DisplayResourceInfo = {
    id: number;
    name: string | null;
    categoryName?: string | null;
};

type BackendStepInput = {
    originalTreatmentStepId: string | null;
    originalTreatmentVariantId: string | null;
    name: string;
    shortDescription: string;
    endMinute: number;
    startMinute: number;
    duration: number;
    acceptablePractitioners: {
        categoryId: number;
        practitionerIds?: number[];
    }[] | null; // Null = no preference
    acceptableRooms: {
        categoryId: number;
        roomIds?: number[];
    }[] | null; // Null = no preference
    acceptableEquipments: {
        categoryId: number;
        equipmentIds?: number[];
    }[] | null; // Null = no preference
};

const MAX_DATE_RANGE_DAYS = 31;

interface DateRangePickerProps {
    dateRange: { from: LocalDateTime | null; to: LocalDateTime | null };
    setDateRange: (range: { from: LocalDateTime; to: LocalDateTime }) => void;
    disabled?: (date: Date) => boolean;
    fromDate?: Date;
}

function DateRangePicker({ dateRange, setDateRange, disabled, fromDate }: DateRangePickerProps) {
    // Track which date we're selecting next: 'from' or 'to'
    const [selectionPhase, setSelectionPhase] = useState<'from' | 'to'>('from');
    const [tempFrom, setTempFrom] = useState<Date | undefined>(
        dateRange.from ? new Date(dateRange.from.getDisplayDate()) : undefined
    );
    const [tempTo, setTempTo] = useState<Date | undefined>(
        dateRange.to ? new Date(dateRange.to.getDisplayDate()) : undefined
    );
    const [isOpen, setIsOpen] = useState(false);

    // Reset selection when popover opens
    useEffect(() => {
        if (isOpen) {
            // Start fresh - user selects 'from' first
            setSelectionPhase('from');
            setTempFrom(dateRange.from ? new Date(dateRange.from.getDisplayDate()) : undefined);
            setTempTo(dateRange.to ? new Date(dateRange.to.getDisplayDate()) : undefined);
        }
    }, [isOpen]);

    const handleDayClick = (day: Date) => {
        if (selectionPhase === 'from') {
            setTempFrom(day);
            setTempTo(undefined);
            setSelectionPhase('to');
        } else {
            let finalFrom = tempFrom;
            let finalTo = day;
            
            if (tempFrom && day < tempFrom) {
                finalFrom = day;
                finalTo = tempFrom;
            }

            if (finalFrom && finalTo && differenceInCalendarDays(finalTo, finalFrom) >= MAX_DATE_RANGE_DAYS) {
                finalTo = addDays(finalFrom, MAX_DATE_RANGE_DAYS - 1);
            }
            
            setTempFrom(finalFrom);
            setTempTo(finalTo);
            
            // Apply the selection
            if (finalFrom) {
                const fromStr = format(finalFrom, "yyyy-MM-dd");
                const newFrom = new LocalDateTime(`${fromStr} 00:00`, "Europe/Berlin");
                
                let newTo: LocalDateTime;
                if (finalTo) {
                    const toStr = format(finalTo, "yyyy-MM-dd");
                    newTo = new LocalDateTime(`${toStr} 00:00`, "Europe/Berlin").clone().addDays(1).addMinutes(-1);
                } else {
                    newTo = newFrom.clone().addDays(1).addMinutes(-1);
                }
                
                setDateRange({ from: newFrom, to: newTo });
            }
            
            // Reset to 'from' for next selection cycle
            setSelectionPhase('from');
            // Close the popover after selection is complete
            setIsOpen(false);
        }
    };

    // Determine which dates to highlight
    const selectedRange = useMemo(() => {
        if (selectionPhase === 'from') {
            // When in 'from' phase, don't show any selection (user is starting fresh)
            return undefined;
        }
        if (tempFrom && tempTo) {
            return { from: tempFrom, to: tempTo };
        } else if (tempFrom) {
            // Only show single date when we're waiting for 'to' selection
            return { from: tempFrom, to: tempFrom };
        }
        return undefined;
    }, [tempFrom, tempTo, selectionPhase]);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant={'outline'}
                    className={cn(
                        'w-full justify-start text-left font-normal',
                        !dateRange.from && 'text-muted-foreground'
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                        dateRange.to ? (
                            <>
                                {format(new Date(dateRange.from.getDisplayDate()), 'dd. MMM yyyy', { locale: de })} -{' '}
                                {format(new Date(dateRange.to.getDisplayDate()), 'dd. MMM yyyy', { locale: de })}
                            </>
                        ) : (
                            format(new Date(dateRange.from.getDisplayDate()), 'dd. MMM yyyy', { locale: de })
                        )
                    ) : (
                        <span>Zeitraum auswählen</span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <div className="p-3 border-b h-[52px] flex flex-col justify-center">
                    <p className="text-sm text-muted-foreground">
                        {selectionPhase === 'from' 
                            ? 'Wähle das Startdatum' 
                            : 'Wähle das Enddatum'}
                    </p>
                    <p className="text-xs text-muted-foreground h-4">
                        {tempFrom && selectionPhase === 'to' 
                            ? `Start: ${format(tempFrom, 'dd. MMM yyyy', { locale: de })} (max. ${MAX_DATE_RANGE_DAYS} Tage)`
                            : '\u00A0'
                        }
                    </p>
                </div>
                <Calendar
                    mode="range"
                    locale={de}
                    selected={selectedRange}
                    onDayClick={handleDayClick}
                    disabled={(date) => {
                        if (disabled?.(date)) return true;
                        if (selectionPhase === 'to' && tempFrom) {
                            return differenceInCalendarDays(date, tempFrom) >= MAX_DATE_RANGE_DAYS;
                        }
                        return false;
                    }}
                    hidden={fromDate ? { before: fromDate } : undefined}
                    numberOfMonths={2}
                    defaultMonth={tempFrom || new Date()}
                />
            </PopoverContent>
        </Popover>
    );
}


interface AvailableSlotItemProps {
    slot: AvailableSlotData;
    onSelect: (slot: AvailableSlotData) => void;
    variant?: 'best' | 'second-best' | 'third-best';

}

function AvailableSlotItem({ slot, onSelect, variant }: AvailableSlotItemProps) {
    console.log("availableSlotItem");
    console.log(slot)

    const displayPractitioners: DisplayResourceInfo[] = uniqueOnId(slot.stepConstraints.flatMap((step) => step.practitioners.map(p => ({
        id: p.id,
        name: `${p.name || ''}`.trim() || `Behandler ${p.id}`,
        categoryName: p.categoryName
    }))));

    return (
        <button
            onClick={() => onSelect(slot)}
            className={cn(
                "block w-full text-left px-2 py-4 border rounded-md hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                variant === 'best' && "bg-green-50 border-green-400 hover:bg-green-50",
                variant === 'second-best' && "bg-yellow-50 border-yellow-200 hover:bg-yellow-100",
                variant === 'third-best' && "bg-red-50 border-red-400 hover:bg-red-100"
            )}
        >
            <div className='w-full flex items-center justify-between gap-2'>
                {/* Zeit und Behandler in einer Zeile */}
                <div className="flex items-center gap-2 flex-1">
                    <span className="font-semibold text-xs whitespace-nowrap">
                        {slot.from.format("HH:mm")} - {slot.to.format("HH:mm")}
                    </span>
                    <div className="flex flex-wrap gap-1">
                        {displayPractitioners.map((resource) => (
                            <Badge
                                variant="secondary"
                                key={`practitioner-${resource.id}`}
                                className="px-1.5 py-0 text-[11px] font-normal"
                                title={resource.categoryName ?? undefined}
                            >
                                <PractitionerNameWithCategory
                                    name={resource.name || `ID: ${resource.id}`}
                                    categoryName={resource.categoryName}
                                />
                            </Badge>
                        ))}
                    </div>
                </div>
                {/* Badge rechts */}
                {!!variant && (
                    <>
                        {variant === 'best' && <Badge variant='outline' className='text-[10px] bg-green-50 border-green-400 text-green-900 !py-0'>Best</Badge>}
                        {variant === 'second-best' && <Badge variant='outline' className='text-[10px] bg-yellow-50 border-yellow-400 text-yellow-900 !py-0'>Good</Badge>}
                        {variant === 'third-best' && <Badge variant='outline' className='text-[10px] bg-red-50 border-red-400 text-red-900 !py-0'>Not good</Badge>}
                    </>
                )}
            </div>
        </button>
    );
}

// State to hold user selections before confirming
type ResourceAssignmentState = Record<
    string, // Step ID
    {
        practitionerId?: string; // Store selected ID as string for Select compatibility
        roomId?: string;
        // Assuming multiple equipment might be assigned per step, but the API only returns one list per slot.
        // For simplicity now, let's assume only ONE piece of equipment needs selection if constrained. Adapt if multiple are needed.
        equipmentId?: string;
    }
>;

interface ResourceAssignmentViewProps {
    storeId: number;
    originalSteps: Step[]; // Steps from the Zustand store
    selectedSlot: AvailableSlotData; // The chosen general slot
    onAssignResources: (updatedSteps: Step[]) => void; // Callback when confirmed
    onGoBack: () => void; // Callback to go back to slot selection
}

function ResourceAssignmentView({
    storeId,
    originalSteps,
    selectedSlot,
    onAssignResources,
    onGoBack
}: ResourceAssignmentViewProps) {
    const [assignments, setAssignments] = useState<ResourceAssignmentState>({});

    useEffect(() => {
        // Pre-select random practitioners, rooms, and equipment for each step
        const initialAssignments: ResourceAssignmentState = {};

        // Sort step constraints by time
        const sortedStepConstraints = selectedSlot.stepConstraints
            .sort((a, b) => a.startLocalDateTime.valueOf() - b.startLocalDateTime.valueOf());

        // Helper function to find the best resource that can serve the most steps
        const findBestResource = (
            resourceType: 'practitioners' | 'rooms' | 'equipment',
        ): Record<string, string> => {
            // Group resources by category and count how many steps each resource can serve
            const categoryResourceSpans: Record<number, Record<number, number>> = {}; // [categoryId] -> [resourceId] -> [# steps can do]
            const assignments: Record<string, string> = {}; // [stepId] -> [resourceId]

            // Get all available resources for each step, grouped by category
            sortedStepConstraints.forEach((stepConstraint) => {
                const stepResourceConstraint = stepConstraint[resourceType];
                stepResourceConstraint.forEach((resourceConstraint) => {
                    const resourceId = resourceConstraint.id;
                    const resourceCategoryId = resourceConstraint.categoryId;

                    if (!(resourceCategoryId in categoryResourceSpans)) {
                        categoryResourceSpans[resourceCategoryId] = {};
                    }
                    if (!(resourceId in categoryResourceSpans[resourceCategoryId])) {
                        categoryResourceSpans[resourceCategoryId][resourceId] = 0;
                    }
                    categoryResourceSpans[resourceCategoryId][resourceId] += 1;
                });
            });

            // For each category, find the most available resource (serves most steps)
            const bestResourcePerCategory: Record<number, number> = {}; // [categoryId] -> [resourceId]
            Object.entries(categoryResourceSpans).forEach(([categoryId, resourceSpans]) => {
                let maxSteps = 0;
                const bestResourceIds: number[] = [];

                // First pass: find the maximum step count
                Object.entries(resourceSpans).forEach(([resourceId, stepCount]) => {
                    if (stepCount > maxSteps) {
                        maxSteps = stepCount;
                    }
                });

                // Second pass: collect all resources that match the maximum
                Object.entries(resourceSpans).forEach(([resourceId, stepCount]) => {
                    if (stepCount === maxSteps) {
                        bestResourceIds.push(Number(resourceId));
                    }
                });

                // Randomly pick one of the best resources
                const randomIndex = Math.floor(Math.random() * bestResourceIds.length);
                bestResourcePerCategory[Number(categoryId)] = bestResourceIds[randomIndex];
            });

            // Assign the best resource per category to each step
            sortedStepConstraints.forEach((stepConstraint, idx) => {
                const stepResourceConstraint = stepConstraint[resourceType];
                const originalStep = originalSteps[idx];

                // Find which category's best resource is available for this step
                for (const resourceConstraint of stepResourceConstraint) {
                    const categoryId = resourceConstraint.categoryId;
                    const bestResourceId = bestResourcePerCategory[categoryId];

                    // Check if the best resource for this category is available for this step
                    if (resourceConstraint.id === bestResourceId) {
                        assignments[originalStep.id] = bestResourceId.toString();
                        break;
                    }
                }

                // If no best resource was found, fall back to random available
                if (!assignments[originalStep.id] && stepResourceConstraint.length > 0) {
                    const randomIndex = Math.floor(Math.random() * stepResourceConstraint.length);
                    assignments[originalStep.id] = stepResourceConstraint[randomIndex].id.toString();
                }
            });

            return assignments;
        };

        // Assign practitioners
        const practitionerAssignments = findBestResource('practitioners');

        // Rooms are preselected by the server, using the same allocation as customer bookings.
        // Without a server suggestion (rooms block timeslots), the best available room is preselected.
        const roomAssignments: Record<string, string> = findBestResource('rooms');
        sortedStepConstraints.forEach((stepConstraint, idx) => {
            const originalStep = originalSteps[idx];
            if (originalStep && stepConstraint.suggestedRoomId !== null) {
                roomAssignments[originalStep.id] = stepConstraint.suggestedRoomId.toString();
            }
        });

        // Assign equipment
        const equipmentAssignments = findBestResource('equipment');

        // Combine all assignments
        originalSteps.forEach((step) => {
            initialAssignments[step.id] = {
                practitionerId: practitionerAssignments[step.id],
                roomId: roomAssignments[step.id],
                equipmentId: equipmentAssignments[step.id],
            };
        });

        setAssignments(initialAssignments);
    }, []);

    // Function to update the selection state
    const handleSelectChange = (
        stepId: string,
        resourceType: 'practitioner' | 'room' | 'equipment',
        resourceId: string, // The selected ID (string from Select component)
        setForAllSteps?: boolean,
    ) => {
        if (setForAllSteps) {
            const newAssignments = { ...assignments };
            originalSteps.forEach((step, idx) => {
                const stepConstraints = selectedSlot.stepConstraints.sort((a, b) => a.startLocalDateTime.valueOf() - b.startLocalDateTime.valueOf())[idx];
                let shouldSet = false;
                if (resourceType === 'practitioner') {
                    if (!stepConstraints || stepConstraints.practitioners.length === 0) {
                        shouldSet = false;
                    } else {
                        const allowedIds = stepConstraints.practitioners.map((p) => p.id.toString());
                        shouldSet = allowedIds.includes(resourceId);
                    }
                } else if (resourceType === 'room') {
                    if (!stepConstraints || stepConstraints.rooms.length === 0) {
                        shouldSet = false;
                    } else {
                        const allowedIds = stepConstraints.rooms.map((r) => r.id.toString());
                        shouldSet = allowedIds.includes(resourceId);
                    }
                } else if (resourceType === 'equipment') {
                    if (!stepConstraints || stepConstraints.equipment.length === 0) {
                        shouldSet = false;
                    } else {
                        const allowedIds = stepConstraints.equipment.map((e) => e.id.toString());
                        shouldSet = allowedIds.includes(resourceId);
                    }
                }
                if (shouldSet) {
                    newAssignments[step.id] = {
                        ...newAssignments[step.id],
                        [`${resourceType}Id`]: resourceId,
                    };
                }
            });
            setAssignments(newAssignments);
        } else {
            setAssignments(prev => ({
                ...prev,
                [stepId]: {
                    ...prev[stepId],
                    [`${resourceType}Id`]: resourceId,
                }
            }));
        }
    };

    // --- Validation Logic ---
    const isAssignmentComplete = useMemo(() => {
        return originalSteps.every(step => {
            const stepAssignments = assignments[step.id] ?? {};

            // Check practitioner constraint
            const needsPractitioner = (step.acceptablePractitioners?.length ?? 0) > 0;
            if (needsPractitioner && !stepAssignments.practitionerId) return false;

            // Check room constraint
            const needsRoom = (step.acceptableRooms?.length ?? 0) > 0;
            if (needsRoom && !stepAssignments.roomId) return false;

            // Check equipment constraint
            const needsEquipment = (step.acceptableEquipments?.length ?? 0) > 0;
            if (needsEquipment && !stepAssignments.equipmentId) return false; // Simplified: assuming one selection needed if constrained

            return true;
        });
    }, [originalSteps, assignments]);
    // --- End Validation ---

    // --- Confirmation Handler ---
    const handleConfirm = useCallback(() => {
        // Create the updated steps array based on selections
        const allPractitioners = selectedSlot.stepConstraints.flatMap((stepConstraint) => stepConstraint.practitioners);
        const allRooms = selectedSlot.stepConstraints.flatMap((stepConstraint) => stepConstraint.rooms);
        const allEquipments = selectedSlot.stepConstraints.flatMap((stepConstraint) => stepConstraint.equipment);
        const updatedSteps = originalSteps.map(step => {
            const stepAssignments = assignments[step.id];
            if (!stepAssignments) return step; // No assignments for this step

            // Find the full resource objects from the selectedSlot based on assigned IDs
            const selectedPractitioner = allPractitioners.find(p => p.id.toString() === stepAssignments.practitionerId);
            const selectedRoom = allRooms.find(r => r.id.toString() === stepAssignments.roomId);
            const selectedEquipment = allEquipments.find(e => e.id.toString() === stepAssignments.equipmentId);

            return {
                ...step,
                practitioners: selectedPractitioner ? [{
                    id: selectedPractitioner.id, // Ensure ID is string if store expects string
                    name: `${selectedPractitioner.name}`,
                    categoryId: selectedPractitioner.categoryId,
                    categoryName: selectedPractitioner.categoryName,
                }] : step.practitioners,
                rooms: selectedRoom ? [{
                    id: selectedRoom.id,
                    name: selectedRoom.name || '',
                    categoryId: selectedRoom.categoryId,
                    categoryName: selectedRoom.categoryName,
                    equipment: selectedRoom.equipment || [],
                }] : step.rooms,
                equipment: selectedEquipment ? [{
                    id: selectedEquipment.id,
                    name: selectedEquipment.name || '',
                    categoryId: selectedEquipment.categoryId,
                    categoryName: selectedEquipment.categoryName
                }] : step.equipment,
            };
        });

        onAssignResources(updatedSteps); // Pass the fully updated steps back
    }, [selectedSlot, assignments, originalSteps, onAssignResources]);
    // --- End Confirmation ---


    return (
        <div className="flex flex-col h-full overflow-y-auto"> {/* Ensure parent takes height */}
            {/* Header with Back button */}
            <div className="flex items-center justify-between pb-4 border-b mb-4">
                <Button variant="ghost" size="sm" onClick={onGoBack} className="text-sm">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Zurück zu den Terminen
                </Button>
                <p className="text-sm font-medium text-muted-foreground">
                    Ressourcen zuordnen für {selectedSlot.from.format("dd. MMMM yyyy, HH:mm")} Uhr
                </p>
            </div>

            {/* Scrollable area for steps */}
            <div className="flex-grow overflow-y-auto">
                <ScrollArea className="h-full"> {/* Takes remaining space */}
                    <div className="space-y-6"> {/* Increased spacing between steps */}
                        {originalSteps.filter(step => step.minuteEnd > step.minuteStart).sort((a, b) => a.minuteStart - b.minuteStart).map((step, index) => {
                            // Find required category IDs for this step
                            const stepConstraints = selectedSlot.stepConstraints.sort((a, b) => a.startLocalDateTime.valueOf() - b.startLocalDateTime.valueOf())[index];

                            const acceptablePractitioners = step.acceptablePractitioners;
                            const acceptableRooms = step.acceptableRooms;
                            const acceptableEquipments = step.acceptableEquipments;

                            // Filter available resources based on required categories
                            const availablePractitioners = stepConstraints.practitioners;
                            const availableRooms = stepConstraints.rooms;
                            const availableEquipment = stepConstraints.equipment;

                            const stepAssignments = assignments[step.id] ?? {}; // TODO: This is not correct, we need to store the stepAssignments for each step

                            return (
                                <div key={step.id} className="p-4 border rounded-md shadow-sm bg-card">
                                    <p className="font-semibold mb-1">Schritt {index + 1}: {step.name}</p>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        ({step.minuteEnd - step.minuteStart} min)
                                    </p>
                                    <div className="space-y-4">
                                        {/* Practitioner Selection */}
                                        {acceptablePractitioners && acceptablePractitioners.length > 0 && availablePractitioners.length > 0 && (
                                            <div>
                                                <Label htmlFor={`practitioner-${step.id}`} className="text-xs font-medium">Behandler</Label>
                                                <Select
                                                    value={stepAssignments.practitionerId}
                                                    onValueChange={(value) => handleSelectChange(step.id, 'practitioner', value, stepAssignments.practitionerId === undefined)}
                                                >
                                                    <SelectTrigger id={`practitioner-${step.id}`} className="mt-1">
                                                        <SelectValue placeholder="Behandler auswählen..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {availablePractitioners.map(p => (
                                                            <SelectItem key={p.id} value={p.id.toString()}>
                                                                <PractitionerNameWithCategory
                                                                    name={`${p.name || ''}`.trim()}
                                                                    categoryName={p.categoryName}
                                                                />
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {/* Simple validation indicator */}
                                                {!stepAssignments.practitionerId && <p className="text-xs text-destructive mt-1">Auswahl erforderlich</p>}
                                            </div>
                                        )}
                                        {acceptablePractitioners && acceptablePractitioners.length > 0 && availablePractitioners.length === 0 && (
                                            <Alert variant="destructive">
                                                <AlertDescription className="text-xs">No available practitioners found for required category in this slot. ({acceptablePractitioners.map((cat) => cat.categoryId)})</AlertDescription>
                                            </Alert>
                                        )}


                                        {/* Room Selection */}
                                        {acceptableRooms && acceptableRooms.length > 0 && availableRooms.length > 0 && (
                                            <div>
                                                <Label htmlFor={`room-${step.id}`} className="text-xs font-medium">Räume</Label>
                                                <Select
                                                    value={stepAssignments.roomId}
                                                    onValueChange={(value) => handleSelectChange(step.id, 'room', value, stepAssignments.roomId === undefined)}
                                                >
                                                    <SelectTrigger id={`room-${step.id}`} className="mt-1 w-full h-auto min-h-9 whitespace-normal *:data-[slot=select-value]:line-clamp-none *:data-[slot=select-value]:whitespace-normal">
                                                        <SelectValue placeholder="Raum auswählen..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {availableRooms.map(r => (
                                                            <SelectItem key={r.id} value={r.id.toString()} className="whitespace-normal">
                                                                <RoomName
                                                                    name={r.name || `Room ${r.id}`}
                                                                    categoryName={
                                                                        r.categoryName
                                                                            ? `${r.categoryName}${r.equipment && r.equipment.length > 0 ? `, ${r.equipment.map(e => e.name).join(', ')}` : ''}`
                                                                            : null
                                                                    }
                                                                />
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {!stepAssignments.roomId && <p className="text-xs text-destructive mt-1">Auswahl erforderlich</p>}
                                            </div>
                                        )}
                                        {acceptableRooms && acceptableRooms.length > 0 && availableRooms.length === 0 && (
                                            <Alert variant="destructive">
                                                <AlertDescription className="text-xs">No available rooms found for required category in this slot.</AlertDescription>
                                            </Alert>
                                        )}

                                        {/* Equipment Selection (Simplified: assumes one) */}
                                        {acceptableEquipments && acceptableEquipments.length > 0 && availableEquipment.length > 0 && (
                                            <div>
                                                <Label htmlFor={`equipment-${step.id}`} className="text-xs font-medium">Geräte</Label>
                                                <Select
                                                    value={stepAssignments.equipmentId}
                                                    onValueChange={(value) => handleSelectChange(step.id, 'equipment', value, stepAssignments.equipmentId === undefined)}
                                                >
                                                    <SelectTrigger id={`equipment-${step.id}`} className="mt-1">
                                                        <SelectValue placeholder="Gerät auswählen..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {availableEquipment.map(e => (
                                                            <SelectItem key={e.id} value={e.id.toString()}>
                                                                {e.name || `Equipment ${e.id}`}
                                                                {e.categoryName && <span className="text-xs text-muted-foreground ml-2">({e.categoryName})</span>}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {acceptableEquipments && acceptableEquipments.length > 0 && !stepAssignments.equipmentId && <p className="text-xs text-destructive mt-1">Auswahl erforderlich</p>}
                                            </div>
                                        )}
                                        {acceptableEquipments && acceptableEquipments.length > 0 && availableEquipment.length === 0 && (
                                            <Alert variant="destructive">
                                                <AlertDescription className="text-xs">No available equipment found for required category in this slot.</AlertDescription>
                                            </Alert>
                                        )}

                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>
            </div>

            {/* Footer with Confirm Button */}
            <div className="pt-4 border-t mt-auto"> {/* Pushes button to bottom */}
                <Button
                    onClick={handleConfirm}
                    disabled={!isAssignmentComplete}
                    className="w-full"
                >
                    Bestätigen
                </Button>
            </div>
        </div>
    );
}


// --- Main Dialog Content (Adapted for new Input/Output) ---
interface AppointmentSlotSelectorContentProps {
    storeId: number;
    onSlotSelected: () => void;
    // acceptablePractitionerIds?: number[]; // Add props if UI implemented
    // acceptableRoomIds?: number[];
    // acceptableEquipmentIds?: number[];
}

function AppointmentSlotSelectorContent({
    storeId,
    onSlotSelected,
    // acceptablePractitionerIds, // Receive from props
    // acceptableRoomIds,
    // acceptableEquipmentIds
}: AppointmentSlotSelectorContentProps) {
    const {
        steps: originalSteps, // Rename to avoid conflict
        dateRange,
        setDateRange,
        setStartDate,
        setStartTime,
        setSteps,
        refreshSelectableResources,
        selectedBookableItems,
        mode,
        appointmentId,
    } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            steps: state.steps,
            selectedBookableItems: state.selectedBookableItems,
            mode: state.mode,
            appointmentId: state.appointmentId,
            dateRange: state.dateRange,
            setDateRange: state.setDateRange,
            setStartDate: state.setStartDate,
            setStartTime: state.setStartTime,
            setSteps: state.setSteps,
            refreshSelectableResources: state.refreshSelectableResources,
        }))
    );

    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );

    const { timezone } = useUserStore(
        useShallow(state => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    )

    const trpcUtils = trpc.useUtils();

    const hasKontrolle = useMemo(() => selectedBookableItems.some(item =>
        item.treatmentVariantName?.toLowerCase().includes('kontroll')
        || item.addOns.some(addOn => addOn.variantName?.toLowerCase().includes('kontroll'))
    ), [selectedBookableItems]);

    // --- Component State ---
    const [view, setView] = useState<'selectSlot' | 'assignResources'>('selectSlot');
    const [selectedSlotForAssignment, setSelectedSlotForAssignment] = useState<AvailableSlotData | null>(null);

    // --- State for collapsible dates in Slot Selection view ---
    const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>({});
    const [showOnlyBest, setShowOnlyBest] = useState(false);
    const [selectedTimePeriods, setSelectedTimePeriods] = useState<Set<'morning' | 'midday' | 'evening'>>(new Set());

    const toggleDateExpansion = (dayKey: string) => {
        setExpandedDates(prev => ({
            ...prev, // Keep existing states
            [dayKey]: !prev[dayKey] // Toggle the specific date (defaults to true if it wasn't present)
        }));
    };

    // Prepare steps (Keep as is from previous answer)
    const querySteps = useMemo((): BackendStepInput[] => {
        return originalSteps
            .filter(step => {
                const duration = step.minuteEnd - step.minuteStart;
                if (duration <= 0) {
                    return false;
                }
                return true;
            })
            .map(step => {
                const duration = step.minuteEnd - step.minuteStart;
                return {
                    originalTreatmentStepId: step.originalTreatmentStepId,
                    originalTreatmentVariantId: step.treatmentVariantId,
                    name: step.name,
                    shortDescription: step.shortDescription,
                    startMinute: step.minuteStart,
                    endMinute: step.minuteEnd,
                    duration: duration,
                    acceptablePractitioners: step.acceptablePractitioners,
                    acceptableRooms: step.acceptableRooms,
                    acceptableEquipments: step.acceptableEquipments,
                };
            })
    }, [originalSteps]);

    const {
        data: practitionersResponse,
        isLoading: isLoadingPractitioners,
        isFetching: isFetchingPractitioners,
    } = trpc.practitioners.getAll.useQuery(
        {
            pageSize: 1000,
            showArchived: false,
            filters: {
                storeId: storeId.toString()
            },
        },
        {
            enabled: querySteps.length > 0 && !!storeId && (dateRange.to > dateRange.from),
            suspense: true,
        }
    );

    const [selectedPractitioners, setSelectedPractitioners] = useState<number[]>([]);
    const practitionersOptions = useMemo(() => {
        if (practitionersResponse?.data) {
            return practitionersResponse.data.map((practitioner) => {
                return {
                    id: practitioner.id,
                    label: `${practitioner.title ? `${practitioner.title} ` : ''}${practitioner.firstName} ${practitioner.lastName}`,
                    categoryName: practitioner.practitioner_category?.name ?? null,
                }
            })
        }
        return [];
    }, [practitionersResponse]);

    const isDateRangeValid = useMemo((): boolean => {
        if (!dateRange.from || !dateRange.to) {
            return false;
        }
        if (dateRange.to.valueOf() <= dateRange.from.valueOf()) {
            return false;
        }
        const fromDate = new Date(dateRange.from.getDisplayDate());
        const toDate = new Date(dateRange.to.getDisplayDate());
        if (differenceInCalendarDays(toDate, fromDate) >= MAX_DATE_RANGE_DAYS) {
            return false;
        }
        return true;
    }, [dateRange]);

    // tRPC Query Hook
    const { data: availableSlots, isLoading, isFetching, error, isError, refetch } = trpc.appointments.getAvailabilities.useQuery(
        {
            storeId: storeId,
            from: dateRange.from?.toJson() || '',
            to: dateRange.to?.clone().addDays(1).toJson() || '',
            steps: querySteps.map((step) => ({
                originalTreatmentStepId: step.originalTreatmentStepId || undefined,
                originalTreatmentVariantId: step.originalTreatmentVariantId || undefined,
                name: step.name,
                shortDescription: step.shortDescription,
                startMinute: step.startMinute,
                endMinute: step.endMinute,
                duration: step.duration,
                acceptablePractitioners: step.acceptablePractitioners,
                acceptableRooms: step.acceptableRooms,
                acceptableEquipments: step.acceptableEquipments,
            })),
            acceptablePractitionerIds: selectedPractitioners.length > 0 ? selectedPractitioners : null,
            acceptableRoomIds: null,
            acceptableEquipmentIds: null,
            existingAppointmentId: mode === 'modify' ? appointmentId ?? undefined : undefined,
        },
        {
            enabled: querySteps.length > 0 && !!storeId && isDateRangeValid === true,
            refetchOnWindowFocus: true,
        }
    );

    const handleFromDateChange = (newDate: Date | undefined) => {
        if (newDate) {
            const newDateStr = format(newDate, "yyyy-MM-dd");
            const newFrom = new LocalDateTime(`${newDateStr} 00:00`, "Europe/Berlin");
            let currentTo = dateRange.to;

            if (!currentTo || currentTo < newFrom) {
                currentTo = newFrom.clone().addDays(1).addMinutes(-1);
            }

            const fromNative = new Date(newFrom.getDisplayDate());
            const toNative = new Date(currentTo.getDisplayDate());
            if (differenceInCalendarDays(toNative, fromNative) >= MAX_DATE_RANGE_DAYS) {
                const clampedStr = format(addDays(fromNative, MAX_DATE_RANGE_DAYS - 1), "yyyy-MM-dd");
                currentTo = new LocalDateTime(`${clampedStr} 23:59`, "Europe/Berlin");
            }

            setDateRange({ from: newFrom, to: currentTo });
        }
    };
    const handleToDateChange = (newDate: Date | undefined) => {
        if (newDate) {
            const newDateStr = format(newDate, "yyyy-MM-dd");
            let newTo = new LocalDateTime(`${newDateStr} 00:00`, "Europe/Berlin").clone().addDays(1).addMinutes(-1);
            const currentFrom = dateRange.from || newTo;

            if (newTo < currentFrom) {
                newTo = currentFrom.clone().addDays(1).addMinutes(-1);
            }

            const fromNative = new Date(currentFrom.getDisplayDate());
            const toNative = new Date(newTo.getDisplayDate());
            if (differenceInCalendarDays(toNative, fromNative) >= MAX_DATE_RANGE_DAYS) {
                const clampedStr = format(addDays(fromNative, MAX_DATE_RANGE_DAYS - 1), "yyyy-MM-dd");
                newTo = new LocalDateTime(`${clampedStr} 23:59`, "Europe/Berlin");
            }

            setDateRange({ from: currentFrom, to: newTo });
        }
    };

    // Slot selection handler (Accepts AvailableSlotData)
    const handleSelectSlot = (slot: AvailableSlotData) => {
        setSelectedSlotForAssignment(slot); // Store the selected slot
        setView('assignResources'); // Change the view
    };

    const handleGoBackToSlots = () => {
        setSelectedSlotForAssignment(null); // Clear selection
        setView('selectSlot'); // Go back to slot selection view
    };

    const handleResourcesAssigned = (updatedSteps: Step[]) => {
        if (!selectedSlotForAssignment) return; // Should not happen, but safety check

        setSteps(updatedSteps); // Update the steps in the Zustand store
        setStartDate(selectedSlotForAssignment.from); // Set the final start date/time
        setStartTime(selectedSlotForAssignment.from.format("HH:mm"));
        onSlotSelected(); // Close the dialog (process complete)

        if (selectedStore?.id) {
            refreshSelectableResources(selectedStore.id, timezone, trpcUtils);
        }
    };

    const today = startOfDay(new Date());

    // Group slots by date (keep the grouping logic)
    const groupedSlots = useMemo(() => {
        if (!availableSlots) return {};
        return availableSlots.reduce((acc, slot) => {
            const firstStep = slot.steps[0];
            const lastStep = slot.steps[slot.steps.length - 1];
            const startLocalDatetime = LocalDateTime.deserialize(firstStep.start);
            const endLocalDatetime = LocalDateTime.deserialize(lastStep.end);

            const dayDate = new Date(startLocalDatetime.getDisplayDateTime());
            const dayKey = format(dayDate, "yyyy-MM-dd");

            const stepConstraints: AvailableSlotData['stepConstraints'] = slot.steps.map((step) => {
                const seenPractitionerIds = new Set<number>();
                const seenRoomIds = new Set<number>();
                const seenEquipmentIds = new Set<number>();
                // console.log(step.practitioners);
                return {
                    startLocalDateTime: LocalDateTime.deserialize(step.start),
                    endLocalDateTime: LocalDateTime.deserialize(step.end),
                    practitioners: step.practitioners.filter((p) => {
                        if (seenPractitionerIds.has(p.id)) {
                            return false;
                        }
                        seenPractitionerIds.add(p.id);
                        return true;
                    }).map((p) => ({
                        id: p.id,
                        name: p.name,
                        categoryId: p.categoryId,
                        categoryName: p.categoryName,
                    })),
                    rooms: step.rooms.filter((r) => {
                        if (seenRoomIds.has(r.id)) {
                            return false;
                        }
                        seenRoomIds.add(r.id);
                        return true;
                    }).map((r) => ({
                        id: r.id,
                        name: r.name,
                        categoryId: r.categoryId,
                        categoryName: r.categoryName,
                        equipment: r.equipment,
                    })),
                    suggestedRoomId: step.suggestedRoomId ?? null,
                    equipment: step.equipments.filter((e) => {
                        if (seenEquipmentIds.has(e.id)) {
                            return false;
                        }
                        seenEquipmentIds.add(e.id);
                        return true;
                    }).map((e) => ({
                        id: e.id,
                        name: e.name,
                        categoryId: e.categoryId,
                        categoryName: e.categoryName,
                    })),
                }
            });

            if (!acc[dayKey]) {
                acc[dayKey] = [];
            }
            acc[dayKey].push({
                timeslotPriorityCategory: slot.timeslotPriorityCategory,
                from: startLocalDatetime,
                to: endLocalDatetime,
                stepConstraints,
            });
            return acc;
        }, {} as Record<string, AvailableSlotData[]>);
    }, [availableSlots]);

    return (
        <DialogContent className="sm:max-w-[1000px] h-[80vh] flex flex-col">
            {view === 'selectSlot' && (
                <>
                    <DialogHeader>
                        <DialogTitle>Wähle einen verfügbaren Termin</DialogTitle>
                        <DialogDescription>
                            Wähle einen Zeitraum. Klicke ein Datum um verfügbare Termine für diesen Tag zu sehen.
                        </DialogDescription>
                    </DialogHeader>

                    <div className='flex flex-row items-center gap-4'>
                        <Popover>
                            <PopoverTrigger asChild>
                                <div>
                                    <Button variant="outline" role="combobox" className="w-full justify-between">
                                        {selectedPractitioners.length > 0 ? `${selectedPractitioners.length} practitioners(s) selected` : "Nach Behandler filtern..."}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[300px] overflow-y-auto p-0">
                                <Command>
                                    <CommandInput placeholder="Behandler suchen..." />
                                    <CommandList>
                                        <CommandEmpty>Kein Behandler gefunden.</CommandEmpty>
                                        <CommandGroup>
                                            {isLoadingPractitioners ? <CommandItem disabled>Loading practitioners...</CommandItem> :
                                                practitionersOptions.map((practitioner) => {
                                                    const isSelected = selectedPractitioners.includes(practitioner.id);
                                                    return (
                                                        <CommandItem
                                                            key={`practitioner-option-${practitioner.id}`}
                                                            value={`${practitioner.label} ${practitioner.categoryName ?? ''} ${practitioner.id}`}
                                                            onSelect={() => {
                                                                const currentSelectedIds = selectedPractitioners;
                                                                if (isSelected) {
                                                                    setSelectedPractitioners(currentSelectedIds.filter(id => id !== practitioner.id));
                                                                } else {
                                                                    setSelectedPractitioners([...currentSelectedIds, practitioner.id]);
                                                                }
                                                            }}
                                                        >
                                                            <Checkbox className="mr-2" checked={isSelected} onCheckedChange={(checked) => { /* Handled by onSelect now */ }} />
                                                            <PractitionerNameWithCategory
                                                                name={practitioner.label}
                                                                categoryName={practitioner.categoryName}
                                                            />
                                                        </CommandItem>
                                                    );
                                                })}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>

                        {/* Date Range Picker & Refresh */}
                        <div className="flex flex-col sm:flex-row gap-4 items-center">
                            <div className='flex-1 w-full'>
                                <div className='flex flex-row gap-4 items-center'>
                                    <div>
                                        <DateRangePicker
                                            dateRange={dateRange}
                                            setDateRange={setDateRange}
                                            disabled={(date) => date < today}
                                            fromDate={today}
                                        />
                                    </div>
                                    <Button
                                        disabled={isFetching || !isDateRangeValid}
                                        onClick={() => {
                                            if (!isFetching && isDateRangeValid) {
                                                refetch();
                                            }
                                        }}
                                        variant="outline"
                                        size="icon"
                                        title="Termine aktualisieren"
                                    >
                                        {isFetching ? (
                                            <Loader2 className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                                        ) : (
                                            <RefreshCcw className='h-4 w-4' />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filter Sektion */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowOnlyBest(!showOnlyBest)}
                                className={cn(
                                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                    showOnlyBest ? "bg-[#FF6439]" : "bg-gray-200"
                                )}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                        showOnlyBest ? "translate-x-6" : "translate-x-1"
                                    )}
                                />
                            </button>
                            <span className="text-sm font-medium">nur "Best" anzeigen</span>
                        </div>

                        <Separator orientation="vertical" className='!h-6' />

                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-muted-foreground">Tageszeit:</span>
                            <Button
                                variant={selectedTimePeriods.has('morning') ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                    const newSet = new Set(selectedTimePeriods);
                                    if (newSet.has('morning')) {
                                        newSet.delete('morning');
                                    } else {
                                        newSet.add('morning');
                                    }
                                    setSelectedTimePeriods(newSet);
                                }}
                                className="h-7 text-xs"
                            >
                                Vormittag (8-12)
                            </Button>
                            <Button
                                variant={selectedTimePeriods.has('midday') ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                    const newSet = new Set(selectedTimePeriods);
                                    if (newSet.has('midday')) {
                                        newSet.delete('midday');
                                    } else {
                                        newSet.add('midday');
                                    }
                                    setSelectedTimePeriods(newSet);
                                }}
                                className="h-7 text-xs"
                            >
                                Mittag (12-16)
                            </Button>
                            <Button
                                variant={selectedTimePeriods.has('evening') ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => {
                                    const newSet = new Set(selectedTimePeriods);
                                    if (newSet.has('evening')) {
                                        newSet.delete('evening');
                                    } else {
                                        newSet.add('evening');
                                    }
                                    setSelectedTimePeriods(newSet);
                                }}
                                className="h-7 text-xs"
                            >
                                Abend (16-20)
                            </Button>
                        </div>
                    </div>

                    <Separator />

                    {hasKontrolle && (
                        <Alert className="border-yellow-400 bg-yellow-50 text-yellow-900">
                            <AlertTriangle className="text-yellow-600" />
                            <AlertDescription className="text-yellow-900">
                                Kontrollen sollten möglichst Mo-Do von 8-16 Uhr eingebucht werden. Dadurch können wir die besonders gefragten Termine für unsere anderen Behandlungen freihalten.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Fixed height scrollable area to prevent jumping */}
                    <div className="flex-1 min-h-0 overflow-hidden">
                        <ScrollArea className="h-full pr-4">
                            <div className="space-y-1">
                                {querySteps.length === 0 && !isLoading && (<Alert><AlertTitle>No Valid Steps</AlertTitle><AlertDescription>Add steps with duration.</AlertDescription></Alert>)}
                                {!isDateRangeValid && querySteps.length > 0 && (<Alert><AlertTitle>Zeitraum auswählen</AlertTitle><AlertDescription>Bitte wähle einen Zeitraum aus, um verfügbare Termine zu sehen.</AlertDescription></Alert>)}
                                {isLoading && querySteps.length > 0 && isDateRangeValid && (<> <Skeleton className="h-10 w-full rounded-md mb-2" /> <Skeleton className="h-10 w-full rounded-md mb-2" /> <Skeleton className="h-10 w-full rounded-md" /> </>)} {/* Skeleton for headers */}
                                {isError && querySteps.length > 0 && (<Alert variant="destructive"><AlertTitle>Error</AlertTitle><AlertDescription>{error?.message || 'Unknown error.'}<Button variant="link" size="sm" onClick={() => refetch()} className="p-0 h-auto ml-2">Try again</Button></AlertDescription></Alert>)}
                                {!isLoading && !isError && querySteps.length > 0 && isDateRangeValid && availableSlots?.length === 0 && (<Alert><AlertTitle>Keine Termine gefunden</AlertTitle><AlertDescription>Keine Termine im ausgewählten Zeitraum gefunden.</AlertDescription></Alert>)}

                                {/* --- Display Collapsible Date Groups --- */}
                                {!isLoading && !isError && isDateRangeValid && availableSlots && availableSlots.length > 0 && (
                                    Object.entries(groupedSlots).sort(([dayKeyA, slotsForDayA], [dayKeyB, slotsForDayB]) => new Date(dayKeyA).getTime() - new Date(dayKeyB).getTime()).map(([dayKey, slotsForDay]) => {
                                        const isExpanded = !!expandedDates[dayKey];

                                        const bestTimeslotPriorityCategory: number = slotsForDay.reduce((current, slot) => {
                                            if (slot.timeslotPriorityCategory !== undefined && (current === -1 || slot.timeslotPriorityCategory < current)) {
                                                return slot.timeslotPriorityCategory;
                                            }
                                            return current;
                                        }, -1);

                                        const secondBestTimeslotPriorityCategory: number = slotsForDay.reduce((current, slot) => {
                                            if (!slot.timeslotPriorityCategory || slot.timeslotPriorityCategory === bestTimeslotPriorityCategory) {
                                                return current;
                                            }
                                            return (current === -1 || slot.timeslotPriorityCategory < current) ? slot.timeslotPriorityCategory : current;
                                        }, -1);

                                        const thirdBestTimeslotPriorityCategory: number = slotsForDay.reduce((current, slot) => {
                                            if (!slot.timeslotPriorityCategory
                                                || slot.timeslotPriorityCategory === bestTimeslotPriorityCategory
                                                || slot.timeslotPriorityCategory === secondBestTimeslotPriorityCategory) {
                                                return current;
                                            }
                                            return (current === -1 || slot.timeslotPriorityCategory < current)
                                                ? slot.timeslotPriorityCategory : current;
                                        }, -1);

                                        // Filter slots based on showOnlyBest and selectedTimePeriods
                                        let filteredSlotsForDay = showOnlyBest
                                            ? slotsForDay.filter(slot => slot.timeslotPriorityCategory === bestTimeslotPriorityCategory)
                                            : slotsForDay;

                                        // Apply time period filter if any periods are selected
                                        if (selectedTimePeriods.size > 0) {
                                            filteredSlotsForDay = filteredSlotsForDay.filter(slot => {
                                                // Check if slot matches any of the selected time periods
                                                return Array.from(selectedTimePeriods).some(period => isSlotInTimePeriod(slot, period));
                                            });
                                        }

                                        // Skip rendering this day if no slots remain after filtering
                                        if (filteredSlotsForDay.length === 0) {
                                            return null;
                                        }

                                        return (
                                            <div key={dayKey} className="border-b last:border-b-0 py-1">
                                                <button
                                                    onClick={() => toggleDateExpansion(dayKey)}
                                                    aria-expanded={isExpanded}
                                                    className={cn(
                                                        "flex w-full items-center justify-between py-2 px-1 text-sm font-semibold hover:bg-muted rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                                                        isExpanded && "bg-muted"
                                                    )}
                                                >
                                                    {/* Date and Slot Count */}
                                                    <span className="flex-1 text-left">
                                                        {format(toZonedTime(new Date(dayKey), timezone), 'EEEE, d. MMMM yyyy', { locale: de })}
                                                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                                                            ({filteredSlotsForDay.length} Termin{filteredSlotsForDay.length !== 1 ? 'e' : ''})
                                                        </span>
                                                    </span>
                                                    {/* Expansion Indicator */}
                                                    <ChevronRight
                                                        className={cn(
                                                            "h-4 w-4 transform transition-transform duration-200",
                                                            isExpanded && "rotate-90" // Rotate chevron when expanded
                                                        )}
                                                    />
                                                </button>

                                                {/* Conditionally Rendered Slots */}
                                                {isExpanded && (
                                                    <div className="space-y-1 mt-1 pl-4">
                                                        {filteredSlotsForDay.sort((a, b) => a.from.valueOf() - b.from.valueOf()).map((slot) => {
                                                            const priorityCategory = slot.timeslotPriorityCategory;
                                                            const variant = !!priorityCategory
                                                                ? bestTimeslotPriorityCategory === priorityCategory
                                                                    ? 'best'
                                                                    : secondBestTimeslotPriorityCategory === priorityCategory
                                                                        ? 'second-best'
                                                                        : thirdBestTimeslotPriorityCategory === priorityCategory
                                                                            ? 'third-best'
                                                                            : undefined
                                                                : undefined;
                                                            return (
                                                                <AvailableSlotItem
                                                                    key={slot.from.getDisplayDateTime()}
                                                                    slot={slot}
                                                                    onSelect={handleSelectSlot}
                                                                    variant={variant}
                                                                />
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </>
            )}

            {view === 'assignResources' && selectedSlotForAssignment && (
                <ResourceAssignmentView
                    storeId={storeId}
                    originalSteps={originalSteps}
                    selectedSlot={selectedSlotForAssignment}
                    onAssignResources={handleResourcesAssigned}
                    onGoBack={handleGoBackToSlots}
                />
            )}

        </DialogContent>
    );
}

// --- Main Trigger Component (Keep as is from previous answer) ---
export function AppointmentSlotSelector({
    storeId = 1,
    isOpen: externalIsOpen,
    onOpenChange: externalOnOpenChange
}: {
    storeId?: number;
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
}) {
    const {
        setDateRange,
    } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            setDateRange: state.setDateRange,
        }))
    );

    const { selectedDateRange } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedDateRange: state.selectedDateRange,
        }))
    );

    const [internalIsOpen, setInternalIsOpen] = useState(false);

    // Use external control if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const setIsOpen = externalOnOpenChange || setInternalIsOpen;

    const rawSteps = useCreateOrModifyAppointmentStore(state => state.steps);

    const hasValidSteps = useMemo(() => {
        return rawSteps.some(step => (step.minuteEnd - step.minuteStart) > 0);
    }, [rawSteps]);

    useEffect(() => {
        if (isOpen === true) {
            // Set default date range: today to today + 7 days
            const today = LocalDateTime.now("Europe/Berlin").setHours(0).setMinutes(0);
            const inSevenDays = today.clone().addDays(7).addMinutes(-1);
            setDateRange({
                from: today,
                to: inSevenDays,
            });
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="default"
                    size="lg"
                    disabled={!hasValidSteps}
                    title={!hasValidSteps ? 'Add steps with duration first' : undefined}
                    className={cn(
                        hasValidSteps
                            ? "bg-primary-400 hover:bg-primary-500 text-white"
                            : "bg-gray-300 text-gray-500"
                    )}
                >
                    <CalendarIcon className="mr-2 h-5 w-5" />
                    {!hasValidSteps ? 'Füge zuerst Schritte hinzu' : 'Finde verfügbare Termine'}
                </Button>
            </DialogTrigger>
            {isOpen && <AppointmentSlotSelectorContent storeId={storeId} onSlotSelected={() => setIsOpen(false)} />}
        </Dialog>
    );
}